/**
 * Telefones das pessoas. Número novo é gravado no formato internacional único (E.164, ex.:
 * +5531900000000). Número antigo sem "+" é ambíguo: fica como está até alguém escolher o país, e
 * nunca recebe +55 sozinho. As regras de cada país são locais, sem serviço externo.
 */
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/min';

/** País inicial de um telefone ainda vazio. */
export const PHONE_DEFAULT_COUNTRY = 'BR';

export interface PhoneParts {
  /** Código de duas letras; null = número antigo, país não confirmado. */
  country: string | null;
  national: string;
}

const digitsOf = (value: string) => value.replace(/\D/g, '');

/** Países com o código internacional de cada um. */
export function phoneCountries(): { code: string; callingCode: string }[] {
  return getCountries().map((code) => ({ code, callingCode: getCountryCallingCode(code) }));
}

/** Separa o número gravado em país e número nacional, sem adivinhar o país. */
export function splitPhone(value: string | null | undefined): PhoneParts {
  const text = (value ?? '').trim();
  if (!text) return { country: PHONE_DEFAULT_COUNTRY, national: '' };
  const parsed = text.startsWith('+') ? parsePhoneNumberFromString(text) : undefined;
  return parsed?.country
    ? { country: parsed.country, national: parsed.formatNational() }
    : { country: null, national: text };
}

/** Junta país e número no valor a gravar. Sem país confirmado, o texto segue como foi digitado. */
export function joinPhone(country: string | null, national: string): string {
  const digits = digitsOf(national);
  if (!digits) return '';
  if (!country) return national.trim();
  const code = country as CountryCode;
  return (
    parsePhoneNumberFromString(national, code)?.number ?? `+${getCountryCallingCode(code)}${digits}`
  );
}

/**
 * Máscara enquanto a pessoa digita. Apagar um símbolo da máscara apaga o número anterior a ele,
 * senão a máscara devolveria o símbolo e a pessoa não conseguiria apagar.
 */
export function typePhone(country: string | null, previous: string, typed: string): string {
  if (!country) return typed;
  const erasedMask = typed.length < previous.length && digitsOf(typed) === digitsOf(previous);
  const digits = erasedMask ? digitsOf(typed).slice(0, -1) : digitsOf(typed);
  return digits ? new AsYouType(country as CountryCode).input(digits) : '';
}

/** Valor com "+" no formato único; número ambíguo ou que não fecha fica como veio. */
export function normalizePhone(value: string): string {
  if (!value.startsWith('+')) return value;
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isPossible() ? parsed.number : value;
}

/** Problema de um número novo com país. Sem "+" não há o que conferir: o país não é conhecido. */
export function phoneIssue(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  if (!text.startsWith('+')) return null;
  return parsePhoneNumberFromString(text)?.isPossible()
    ? null
    : 'Telefone incompleto ou longo demais para o país';
}

/** Número para leitura: internacional quando há país; senão, como foi gravado. */
export function displayPhone(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  if (!text.startsWith('+')) return text;
  return parsePhoneNumberFromString(text)?.formatInternational() ?? text;
}
